/**
 * ToastContainer.tsx
 *
 * Renders a stack of toast notifications in the bottom-right corner.
 * Each toast appears when a new notification arrives via Supabase Realtime
 * and auto-dismisses after 4 seconds (configurable in NotificationContext).
 *
 * Visual design:
 *   - Slides in from the right using a CSS animation
 *   - Glassmorphism card matching the CBOps design system
 *   - Shows the notification type icon, title, and a short body excerpt
 *   - Manual close (×) button dismisses early
 *   - A thin teal progress bar drains over the 4-second TTL
 *
 * Usage:
 *   <ToastContainer /> — place once inside DashboardLayout (already wired)
 */

import React, { useEffect, useState } from 'react';
import { X, Bell, CalendarDays, TrendingUp, CheckSquare, LifeBuoy, DollarSign, AtSign, Megaphone, AlertTriangle, ShieldAlert, Flag, Target } from 'lucide-react';
import { useNotifications } from '../../context/NotificationContext';
import type { NotificationType } from '../../types';

// ── Toast TTL must match TOAST_TTL_MS in NotificationContext ──────────────────
const TOAST_TTL_MS = 4000;

// ── Icon lookup (same mapping as NotificationBell) ────────────────────────────
function getToastIcon(type: NotificationType): { icon: React.ReactNode; colour: string } {
  const sz = 16;
  if (type.startsWith('leave_'))   return { icon: <CalendarDays size={sz} />, colour: 'text-blue-300' };
  if (type === 'kpi_blocker')      return { icon: <Flag         size={sz} />, colour: 'text-red-300' };
  if (type.startsWith('kpi_'))     return { icon: <TrendingUp   size={sz} />, colour: 'text-purple-300' };
  if (type === 'project_milestone') return { icon: <Target      size={sz} />, colour: 'text-emerald-300' };
  if (type.startsWith('task_'))    return { icon: <CheckSquare  size={sz} />, colour: 'text-teal-300' };
  if (type.startsWith('ticket_'))  return { icon: <LifeBuoy     size={sz} />, colour: 'text-orange-300' };
  if (type.startsWith('expense_')) return { icon: <DollarSign   size={sz} />, colour: 'text-green-300' };
  if (type === 'budget_threshold') return { icon: <AlertTriangle size={sz} />, colour: 'text-red-300' };
  if (type === 'compliance_alert') return { icon: <ShieldAlert  size={sz} />, colour: 'text-amber-300' };
  if (type === 'mention')          return { icon: <AtSign       size={sz} />, colour: 'text-pink-300' };
  if (type === 'announcement')     return { icon: <Megaphone    size={sz} />, colour: 'text-yellow-300' };
  return                                  { icon: <Bell         size={sz} />, colour: 'text-white/60' };
}

// ── Progress bar that drains from full to 0 over TOAST_TTL_MS ────────────────
function DrainBar() {
  const [width, setWidth] = useState(100);

  useEffect(() => {
    // Use requestAnimationFrame for smooth animation start
    const raf = requestAnimationFrame(() => {
      setWidth(0);
    });
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white/10 overflow-hidden rounded-b-xl">
      <div
        className="h-full bg-teal-400"
        style={{
          width: `${width}%`,
          transition: `width ${TOAST_TTL_MS}ms linear`,
        }}
      />
    </div>
  );
}

// ── Single toast card ─────────────────────────────────────────────────────────
interface ToastCardProps {
  id: string;
  title: string;
  body?: string;
  type: NotificationType;
  onDismiss: (id: string) => void;
}

function ToastCard({ id, title, body, type, onDismiss }: ToastCardProps) {
  const [visible, setVisible] = useState(false);
  const { icon, colour } = getToastIcon(type);

  // Slide-in on mount
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 10);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      role="alert"
      aria-live="polite"
      style={{
        background: 'rgba(15, 23, 42, 0.97)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        border: '1px solid rgba(255,255,255,0.12)',
        transform: visible ? 'translateX(0)' : 'translateX(110%)',
        transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
      }}
      className="relative w-72 rounded-xl shadow-2xl overflow-hidden"
    >
      <div className="flex items-start gap-3 p-4 pr-10">
        <span className={`flex-shrink-0 mt-0.5 ${colour}`}>{icon}</span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-white leading-snug">{title}</p>
          {body && (
            <p className="text-xs text-white/55 mt-0.5 line-clamp-2">{body}</p>
          )}
        </div>
      </div>

      {/* Close button */}
      <button
        onClick={() => onDismiss(id)}
        aria-label="Dismiss notification"
        className="absolute top-2 right-2 p-1 rounded-md text-white/40 hover:text-white hover:bg-white/10 transition-colors"
      >
        <X size={14} />
      </button>

      {/* Drain bar */}
      <DrainBar />
    </div>
  );
}

// ── Container ─────────────────────────────────────────────────────────────────

/**
 * Renders all active toasts stacked from bottom-right.
 * Place this once inside DashboardLayout — it reads from NotificationContext.
 */
export default function ToastContainer() {
  const { toasts, dismissToast } = useNotifications();

  if (toasts.length === 0) return null;

  return (
    <div
      aria-label="Notification toasts"
      className="fixed bottom-6 right-6 z-50 flex flex-col-reverse gap-3 pointer-events-none"
    >
      {toasts.map(t => (
        <div key={t.id} className="pointer-events-auto">
          <ToastCard
            id={t.id}
            title={t.notification.title}
            body={t.notification.body}
            type={t.notification.type}
            onDismiss={dismissToast}
          />
        </div>
      ))}
    </div>
  );
}
